import clsx from 'clsx'
import toast from 'react-hot-toast'
import { useToggle } from 'usehooks-ts'

import { FvLinkButton } from '@fv/client-components'
import { generateString } from '@fv/client-core'
import type { SelectFieldOption } from '@fv/client-types'
import type { EquipmentAccessorial } from '@fv/models/equipment'

import {
  InputAdornment,
  InputGroup,
  InputGroupWrapper,
} from '@/components/inputs/InputGroup'
import { ShipperMultiSelect } from '@/components/inputs/ShipperMultiSelect'
import { ShipperSlider } from '@/components/ShipperSlider'
import EquipmentAccessorialConfig from '@/components/shared/EquipmentAccessorialConfig'
import { MAX_ALT_EQUIP } from '@/constants'
import { useTypeNames } from '@/hooks/settings'
import { useTruckloadEquipmentOptions } from '@/hooks/useEquipmentOptions'
import type { EquipmentTypeEnum } from '@/types'

import {
  type BaseShipmentFormSectionProps,
  pickFormSectionProps,
  ShipmentFormSection,
} from '../shipment-form/ShipmentFormSection'
import type { OptionalShipmentEquipmentFormModel } from './types'
import { composeEquipmentAccessorialsDescription } from './utils'

type Props<T extends OptionalShipmentEquipmentFormModel> =
  BaseShipmentFormSectionProps & {
    value: T
    onChange: (value: T) => void
    isTemplate?: boolean
    pillClassName?: string
    pillClickable?: boolean
    /** Items are skipped, so the load's description and weight are entered here instead. */
    showLoadSummary?: boolean
  }

const toOptionalNumber = (value: string) =>
  value === '' ? undefined : Number(value)

export const ShipmentEquipmentForm = <
  T extends OptionalShipmentEquipmentFormModel,
>(
  props: Props<T>,
) => {
  const { value, onChange, isTemplate, showLoadSummary } = props
  const [configOpen, toggleConfig] = useToggle()
  const { accessorialName } = useTypeNames()
  const { isLoaded, favorites, others, options } =
    useTruckloadEquipmentOptions()
  if (!isLoaded) return null
  const toSelectOpt = (o: EquipmentTypeEnum): SelectFieldOption => ({
    text: o.name,
    value: o.key,
  })
  const selectGroups = [
    ...(isTemplate
      ? [{ label: '', opts: [{ text: 'No preferred equipment', value: '' }] }]
      : []),
    { label: 'Favorites', opts: favorites.map(toSelectOpt) },
    { label: 'All equipment', opts: others.map(toSelectOpt) },
  ].filter(g => g.opts.length)
  const selectedEquipment = value.type
    ? options.find(o => o.key === value.type)
    : isTemplate
      ? undefined
      : options[0]
  const altOptions = options
    .filter(t => t.key !== value.type)
    .map(o => ({
      label: o.name,
      value: o.key,
    }))
  return (
    <ShipmentFormSection
      title="Equipment"
      icon="truck"
      pillClassName={clsx(
        'bg-fv-blue-50 border-2 border-fv-blue-300',
        props.pillClassName,
      )}
      pillClickable={props.pillClickable}
      className="bg-fv-blue-50 !border-fv-blue-200"
      {...pickFormSectionProps(props)}
    >
      <div className="flex gap-x-2 flex-wrap">
        <InputGroup
          inputType="select"
          label="Preferred equipment"
          inputProps={{
            options: selectGroups.map(g => g.opts),
            groups: selectGroups.map(g => g.label),
            name: 'equipmentType',
            value: value.type,
            onChange: e => {
              const type = e.target.value as T['type']
              onChange({
                ...value,
                type,
                ...(!type && {
                  alternateTypes: [],
                  accessorials: [],
                  description: undefined,
                  declaredValue: undefined,
                  weight: undefined,
                }),
              })
            },
          }}
        />

        {selectedEquipment && (
          <InputGroupWrapper
            label="Alternates"
            name="alternateTypes"
            className="flex-1"
          >
            <ShipperMultiSelect
              name="alternateTypes"
              classNames={{ container: 'w-full' }}
              onChange={nextValues => {
                if (nextValues.length > MAX_ALT_EQUIP) {
                  toast.error(
                    `Max of ${MAX_ALT_EQUIP} alternate equipment types.`,
                  )
                  return
                }

                onChange({
                  ...value,
                  alternateTypes: nextValues.map(v => v.value),
                })
              }}
              options={altOptions}
              getOptionLabel={o => o.label}
              getOptionValue={o => o.value}
              placeholder="Optional equipment"
              value={altOptions.filter(o =>
                value.alternateTypes.includes(o.value),
              )}
            />
          </InputGroupWrapper>
        )}
      </div>
      {(showLoadSummary || (isTemplate && selectedEquipment)) && (
        <div className="flex gap-x-2 flex-wrap">
          <InputGroup
            className="flex-1"
            inputType="text"
            label="Description"
            required={!isTemplate}
            inputProps={{
              name: 'equipmentDescription',
              placeholder: 'What are you shipping?',
              value: value.description ?? '',
              onChange: e =>
                onChange({
                  ...value,
                  description: e.target.value || undefined,
                }),
            }}
          />
          <InputGroup
            inputType="amount"
            label="Total weight"
            required={!isTemplate}
            endContent={<InputAdornment label="lbs." position="end" />}
            inputProps={{
              min: 1,
              name: 'equipmentWeight',
              value: value.weight ?? '',
              onChange: e =>
                onChange({
                  ...value,
                  weight: toOptionalNumber(e.target.value),
                }),
            }}
          />
          <InputGroup
            inputType="amount"
            label="Declared value"
            inputProps={{
              isPrice: true,
              min: 1,
              name: 'equipmentDeclaredValue',
              value: value.declaredValue ?? '',
              onChange: e =>
                onChange({
                  ...value,
                  declaredValue: toOptionalNumber(e.target.value),
                }),
            }}
          />
        </div>
      )}
      {selectedEquipment && (
        <div className="flex gap-x-2 border-t border-dashed border-fv-blue pt-4 items-center">
          <span>Equipment Accessorials:</span>
          <FvLinkButton theme="underlined" onClick={toggleConfig}>
            {composeEquipmentAccessorialsDescription(
              accessorialName,
              value.accessorials,
            )}
          </FvLinkButton>
          <ShipperSlider isOpen={configOpen} onClose={toggleConfig}>
            <div className="flex flex-col gap-y-4">
              {/* TODO VP - this is almost duplicated from the `BookingLoad.tsx` */}
              <EquipmentAccessorialConfig
                accessorials={value.accessorials}
                id={generateString(4)}
                accessorialOptions={selectedEquipment.accessorialOptions}
                setAccessorials={({ accessorials }) => {
                  onChange({
                    ...value,
                    accessorials: accessorials as EquipmentAccessorial[],
                  })
                }}
              />
            </div>
          </ShipperSlider>
        </div>
      )}
    </ShipmentFormSection>
  )
}
